import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {useEnv} from "@/hooks/useEnv";
import {useFirebaseAnalytics} from "@/hooks/useFirebaseAnalytics";

const LANGUAGES = [
  { code: "ru", label: "Русский", short: "RU" },
  { code: "kk", label: "Қазақша", short: "KK" },
  { code: "uz", label: "O'zbekcha", short: "UZ" },
  { code: "en", label: "English", short: "EN" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const { appUrl } = useEnv();

  const { logEventToAccount } = useFirebaseAnalytics();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // resolvedLanguage, а не language: в language может лежать региональный код вроде en-GB
  const activeLang = i18n.resolvedLanguage ?? i18n.language;
  const currentLang = LANGUAGES.find((l) => l.code === activeLang) ?? LANGUAGES[0];

  const navItems = [
    { key: "nav_features", anchor: "features" },
    { key: "nav_howItWorks", anchor: "how-it-works" },
    // { key: "nav_downloadApp", anchor: "download" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
            <img
              src="/logo.svg"
              alt="BroMoney"
              className="w-full h-full object-cover object-left"
            />
          </div>
          <span className="font-bold text-xl tracking-tight text-gray-900">
            Bro<span className="text-[#007AFF]">Money</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <li key={item.key}>
              <a
                href={`#${item.anchor}`}
                className="text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {t(item.key)}
              </a>
            </li>
          ))}
        </ul>

        {/* Right: Language switcher + CTA */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors duration-200 cursor-pointer px-3 py-2 rounded-lg hover:bg-gray-50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line text-sm" />
              </div>
              <span>{currentLang.short}</span>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl py-1 z-50 min-w-[140px] shadow-lg">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer whitespace-nowrap flex items-center justify-between ${
                      activeLang === lang.code
                        ? "text-[#007AFF] font-semibold bg-[#eff6ff]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    {lang.label}
                    {activeLang === lang.code && (
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line text-[#007AFF] text-xs" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={appUrl}
            onClick={() => logEventToAccount('button_top')}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#0066DD] text-white text-sm font-semibold px-5 py-2 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-sm" />
            </div>
            {t('cta_account')}
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          aria-label="Toggle menu"
        >
          <span className={`w-5 h-0.5 bg-gray-700 transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`w-5 h-0.5 bg-gray-700 transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`} />
          <span className={`w-5 h-0.5 bg-gray-700 transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4 shadow-lg">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={`#${item.anchor}`}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              {t(item.key)}
            </a>
          ))}
          {/* Mobile language switcher */}
          <div className="flex flex-wrap gap-2 pt-1">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                  activeLang === lang.code
                    ? "bg-[#007AFF] text-white border-[#007AFF]"
                    : "border-gray-200 text-gray-500 hover:border-gray-400"
                }`}
              >
                {lang.short}
              </button>
            ))}
          </div>
          <div className="pt-2">
            <a
              href={appUrl}
              onClick={() => logEventToAccount('button_top')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#0066DD] text-white text-sm font-semibold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 w-fit"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line text-sm" />
              </div>
              {t('cta_account')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
